import React, { useState } from "react";
import PhoneInput, { isValidPhoneNumber, parsePhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { sentDetails } from "../../api/api";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  phoneCountry: "AU",
  message: "",
  service: "",
  terms: false,
};

const emailShape = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// One place decides what a sendable enquiry is, so the button, the messages
// under the fields and the submit guard can never disagree about it. The API
// checks the email shape the same way.
const enquiryErrors = (form) => {
  const errors = {};

  if (!form.name.trim()) errors.name = "Enter the parent / guardian name.";
  if (!form.service) errors.service = "Enter your child’s age.";
  if (!emailShape.test(form.email.trim()))
    errors.email = "Enter an email address we can reply to.";
  // Australia is only where the field starts, not the answer. A number that is
  // not valid for the country the parent left selected would be saved as an
  // Australian number they never gave us.
  if (!isValidPhoneNumber(form.phone || "", form.phoneCountry))
    errors.phone = "Choose your country and enter a valid phone number.";
  if (!form.terms) errors.terms = "Tick the box so we can contact you.";

  return errors;
};

const Contactus1 = () => {
  const [formData, setFormData] = useState(emptyForm);
  const [isSending, setIsSending] = useState(false);
  const [touched, setTouched] = useState({});

  const errors = enquiryErrors(formData);
  const isSendable = Object.keys(errors).length === 0;

  const markTouched = (e) => {
    setTouched((prev) => ({ ...prev, [e.target.name]: true }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (value) => {
    // The library reports undefined while the box holds a partial number, and
    // the validators throw on anything that is not a string.
    setFormData((prev) => ({ ...prev, phone: value ?? "" }));
  };

  const handleCountryChange = (country) => {
    setFormData((prev) => ({ ...prev, phoneCountry: country || "" }));
  };

  const handleTermsChange = (e) => {
    setFormData((prev) => ({ ...prev, terms: e.target.checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSending) return;

    // A parent can reach Send without ever blurring a field, so the guard has
    // to reveal what is missing rather than send half an enquiry.
    if (!isSendable) {
      setTouched({ name: true, email: true, service: true, phone: true, terms: true });
      return;
    }

    // The picker and the number can disagree -- a parent can leave the flag on
    // Australia and paste a +44 number. The number is what gets called back, so
    // it is what the country has to describe.
    const parsed = parsePhoneNumber(formData.phone, {
      defaultCountry: formData.phoneCountry || "AU",
    });

    setIsSending(true);

    try {
      const response = await sentDetails({
        ...formData,
        phoneCountry: parsed?.country || formData.phoneCountry,
      });

      setFormData(emptyForm);
      setTouched({});

      toast.success(response?.message || "Message sent successfully!");
    } catch (err) {
      console.log(err);

      toast.error(
        err?.response?.data?.message || err?.message || "Message could not be sent. Please try again.",
      );
    } finally {
      // A send that fails has to hand the button back, or the form is stuck
      // saying "Sending" with no way to try again.
      setIsSending(false);
    }
  };

  // console.log(formData);

  const sociallogos = [
    {
      name: "facebook",
      imgpath: "/images/header/Facebook.svg",
      url: "https://www.facebook.com/share/17eZ7J3oY9/?mibextid=qi2Omg",
    },
    {
      name: "Instagram",
      imgpath: "/images/header/Instagram.svg",
      url: "https://www.instagram.com/socialspectrum2024?igsh=MW1jaWhzZnNlcHVzbw==",
    },
    {
      name: "LinkedIn",
      imgpath: "/images/header/LinkedIn.svg",
      url: "https://www.linkedin.com/company/socialspectrum/",
    },
    {
      name: "X",
      imgpath: "/images/header/X.svg",
      url: "https://x.com/SocialSpectrum4?s=08",
    },
  ];

  return (
    <div className="w-full flex  items-center justify-center">
      <div className="2xl:container w-full flex flex-col lg:flex-row gap-[56px] md:gap-16">
        <div className="text-left border-[1px] border-outline-variant lg:w-5/12">
          <img
            src={
              process.env.PUBLIC_URL + "/images/photos/contact-warm-welcome.webp"
            }
            alt="Two friends laughing together over coffee at a café"
            className="w-full"
          />
          <div className="pl-6 md:pl-[70px] pr-6 md:pr-12 py-6 md:py-12">
            <div className="flex gap-4 md:gap-6 mb-10">
              <div>
                <img
                  src="images/contactus/mail_i.png"
                  alt=""
                  className="w-8 md:w-full"
                />
              </div>
              <div className="">
                <p className="text-14 md:text-20 font-bold text-headingtext mb-5 font-headline">
                  Email
                </p>
                <p className="text-14 md:text-16 text-ink mb-3">
                  <a href="mailto:admin@socialspectrum.org">
                    admin@socialspectrum.org 
                  </a>
                </p>
                <p className="text-14 md:text-16 text-ink">
                  <a
                    href="mailto:archana.akhaury@socialspectrum.org"
                    className=""
                  >
                    archana.akhaury@socialspectrum.org
                  </a>
                </p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="min-w-8">
                <img
                  src="images/contactus/location_i.png"
                  alt=""
                  className="w-8 md:w-full"
                />
              </div>
              <div>
                <p className="text-16 md:text-20 font-bold text-headingtext mb-5 font-headline">
                  Location
                </p>
                <div className="flex flex-col md:flex-row gap-5 md:gap-[90px] md:gap-[139px]">
                  <p className="text-14 md:text-16 text-ink">
                    Vic 3029, Australia.
                  </p>
                  <div className="flex gap-4">
                    {sociallogos.map((items, i) => (
                      <Link to={items.url} key={i} target="_">
                        <img
                          src={process.env.PUBLIC_URL + items.imgpath}
                          alt={items.name}
                        />
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-left mt-[0px] lg:mt-[60px] px-6 md:px-14 lg:px-[0px]">
          <p className="text-14 md:text-16 font-bold text-heart-teal mb-3 md:mb-5">
            CONTACT US
          </p>
          <p className="text-20 md:text-36 font-bold font-headline">
            Let's Chat! Get in Touch with Us.
          </p>
          <form
            onSubmit={handleSubmit}
            className="mt-8 md:mt-12 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 w-full max-w-4xl mx-auto"
          >
            {/* Parent / Guardian Name */}
            <div className="flex flex-col gap-3 md:gap-4">
              <label className="text-14 md:text-16 text-headingtext font-medium">
                Parent / Guardian Name*
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onBlur={markTouched}
                placeholder="Enter parent / guardian name"
                className="text-12 md:text-16 border-[1px] border-outline-variant rounded-brand px-3 py-3 md:py-4 focus:outline-none focus:border-gray-500 max-w-full md:max-w-[300px]"
                required
              />
              {touched.name && errors.name && (
                <p className="text-12 md:text-14 text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Child's Age */}
            <div className="flex flex-col gap-3 md:gap-4">
              <label className="text-14 md:text-16 text-headingtext font-medium">
                Child’s Age*
              </label>
              <input
                type="number"
                name="service"
                value={formData.service}
                onChange={handleChange}
                onBlur={markTouched}
                placeholder="Enter your child’s age"
                className="text-12 md:text-16 border-[1px] border-outline-variant rounded-brand px-3 py-3 md:py-4 focus:outline-none focus:border-gray-500  max-w-full md:max-w-[300px]"
                required
              />
              {touched.service && errors.service && (
                <p className="text-12 md:text-14 text-red-600">{errors.service}</p>
              )}
            </div>

            {/* Email Address */}
            <div className="flex flex-col gap-3 md:gap-4">
              <label className="text-14 md:text-16 text-headingtext font-medium">
                Email Address*
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={markTouched}
                placeholder="Enter your email ID"
                className="text-14 md:text-16 border-[1px] border-outline-variant rounded-brand px-3 py-3 md:py-4 focus:outline-none focus:border-gray-500  max-w-full md:max-w-[300px]"
                required
              />
              {touched.email && errors.email && (
                <p className="text-12 md:text-14 text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Phone Number */}
            <div className="flex flex-col gap-3 md:gap-4">
              <label className="text-14 md:text-16 text-headingtext font-medium">
                Phone Number*
              </label>
              <div className="w-full relative flex border-[1px] border-outline-variant rounded-brand focus-within:border-gray-500  max-w-full md:max-w-[305px]">
                {/* <select
                  style={{ color: "gray" }}
                  className="bg-transparent text-12 md:text-16 pl-3 py-4 rounded-l-[16px] focus:outline-none max-w-[60px]"
                >
                  <option value="US">US</option>
                  <option value="+1">+1</option>
                  <option value="+44">+44</option>
                  <option value="+61">+61</option>
                </select> */}

                <PhoneInput
                  name="phone"
                  defaultCountry="AU"
                  addInternationalOption={false}
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  onCountryChange={handleCountryChange}
                  onBlur={markTouched}
                  placeholder="Enter your phone number"
                  className="text-14 md:text-16 px-3 py-3 md:py-4 focus:outline-none focus:border-gray-500 outline-none ml-2 border-outline-variant"
                  required
                />
              </div>
              <p
                className={`text-12 md:text-14 -mt-1 md:-mt-2 ${
                  touched.phone && errors.phone ? "text-red-600" : "text-[#41444B]"
                }`}
              >
                {touched.phone && errors.phone
                  ? errors.phone
                  : "Choose your country, then enter the number as you dial it there."}
              </p>
            </div>

            {/* We’re Listening */}
            <div className="flex flex-col md:col-span-2 gap-3 md:gap-4">
              <label className="text-14 md:text-16 text-headingtext font-medium">
                We’re Listening
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="4"
                placeholder="Share your thoughts..."
                className="text-14 md:text-16 border-[1px] border-outline-variant rounded-brand px-3 py-3 md:py-4 focus:outline-none focus:border-gray-500 max-w-[650px]"
              />
            </div>

            {/* Submit Button */}
            <div className="md:col-span-2 flex flex-col md:flex-row gap-8 md:gap-0 justify-between">
              <div className="flex flex-col gap-1 w-full md:w-[53%]">
                <div className="flex gap-4">
                  <input
                    type="checkbox"
                    id="terms"
                    name="terms"
                    required
                    checked={formData.terms}
                    onChange={handleTermsChange}
                    onBlur={markTouched}
                    className="w-4 md:w-6 h-4 md:h-6 mt-[4px] accent-[var(--color-spectrum-blue)] checked:border-spectrum-blue"
                  />
                  <label
                    htmlFor="terms"
                    className="text-14 md:text-16 text-ink"
                  >
                    By checking this box, you agree to our <b>terms</b> and{" "}
                    <b>privacy policy.</b>
                  </label>
                </div>
                {touched.terms && errors.terms && (
                  <p className="text-12 md:text-14 text-red-600">{errors.terms}</p>
                )}
              </div>
              <button
                type="submit"
                disabled={isSending}
                aria-disabled={isSending || !isSendable}
                aria-busy={isSending}
                className={`bg-spectrum-blue text-white font-bold px-6 py-4 rounded-brand transition duration-300 ${
                  isSendable && !isSending
                    ? "hover:opacity-90"
                    : "opacity-60 cursor-not-allowed"
                }`}
              >
                {isSending ? "Sending..." : "Send Message"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Contactus1;
